"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog"
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerTrigger } from "@/components/ui/drawer"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import {
  MapPin,
  AlertTriangle,
  Waves,
  Wind,
  Shield,
  Anchor,
  Leaf,
  Clock,
  DollarSign,
  Wifi,
  Sun,
  Utensils,
  Navigation,
  Users,
  Car,
  TrendingUp,
  Loader2,
  Sparkles,
  X,
  Globe
} from "lucide-react"
import { LocationSearch } from "@/components/location-search"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { createClient } from "@/lib/supabase/client"
import { UnsplashImagePicker } from "@/components/unsplash-image-picker"
import { useToast } from "@/hooks/use-toast"
import { useRouter } from "next/navigation"
import { ToastAction } from "@/components/ui/toast"
import { csrfFetch } from "@/lib/utils/csrf-client"

interface AddSpotModalProps {
  trigger?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

interface DuplicateSpot {
  id: string
  name: string
  location: string
  distance: number
  activity_type?: string | null // 'surfing', 'kitesurfing', 'both', or null
}

export function AddSpotModal({ trigger, open: controlledOpen, onOpenChange }: AddSpotModalProps) {
  const { isMobile } = usePlatform()
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen
  const setOpen = onOpenChange || setInternalOpen

  const [formData, setFormData] = useState({
    location: "",
    country: "",
    latitude: 0,
    longitude: 0,
    description: "", // Auto-generated by AI
  })
  const [duplicates, setDuplicates] = useState<DuplicateSpot[]>([])
  const [existingSpot, setExistingSpot] = useState<DuplicateSpot | null>(null)
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [createdSpotSlug, setCreatedSpotSlug] = useState<string | null>(null)
  const [createdActivityType, setCreatedActivityType] = useState<string | null>(null)
  const [isUpdatingExisting, setIsUpdatingExisting] = useState(false)
  const { toast } = useToast()
  const router = useRouter()

  const [activityType, setActivityType] = useState<"surfing" | "kitesurfing" | "both" | null>(null)
  const [selectedImage, setSelectedImage] = useState<{
    unsplashId: string
    url: string
    thumbUrl: string
    photographerName: string
    photographerUsername: string
    photographerUrl: string
    thumb?: string
  } | null>(null)

  const supabase = createClient()

  const activityTypes = [
    { label: "Surfing", emoji: "🏄‍♂️", value: "surfing" as const },
    { label: "Kitesurfing", emoji: "🪁", value: "kitesurfing" as const },
    { label: "Both", emoji: "🏄‍♂️🪁", value: "both" as const },
  ]

  const handleLocationSelect = async (location: {
    name: string
    country: string
    latitude: number
    longitude: number
  }) => {
    triggerHaptic('impact')
    setFormData({
      ...formData,
      location: location.name,
      country: location.country,
      latitude: location.latitude,
      longitude: location.longitude,
    })

    await checkForDuplicates(location.name, location.latitude, location.longitude)
  }

  const checkForDuplicates = async (locationName: string, lat: number, lng: number) => {
    if (!locationName || !lat || !lng || !supabase) return

    setIsCheckingDuplicates(true)
    try {
      const { data: exactMatches, error: exactError } = await supabase
        .from("surf_spots")
        .select("id, name, location, latitude, longitude")
        .ilike("location", `%${locationName}%`)
        .not("latitude", "is", null)
        .not("longitude", "is", null)
      
      let activityTypesMap: Record<string, string | null> = {}
      if (exactMatches && exactMatches.length > 0) {
        try {
          const { data: spotsWithActivity } = await supabase
            .from("surf_spots")
            .select("id, activity_type")
            .in("id", exactMatches.map(s => s.id))
          
          if (spotsWithActivity) {
            spotsWithActivity.forEach((spot: any) => {
              activityTypesMap[spot.id] = spot.activity_type || null
            })
          }
        } catch (err: any) {
          console.log("activity_type column check failed")
        }
      }

      if (exactError) throw exactError

      if (exactMatches && exactMatches.length > 0) {
        const closestMatch = exactMatches
          .map((spot) => {
            const distance = calculateDistance(
              lat,
              lng,
              Number.parseFloat(spot.latitude as string),
              Number.parseFloat(spot.longitude as string),
            )
            return { ...spot, distance, activity_type: activityTypesMap[spot.id] || null }
          })
          .filter((spot) => spot.distance < 0.5)
          .sort((a, b) => a.distance - b.distance)[0]

        if (closestMatch) {
          setExistingSpot(closestMatch as DuplicateSpot)
          setDuplicates([closestMatch as DuplicateSpot])
          return
        }
      }

      const { data: nearbySpots, error } = await supabase
        .from("surf_spots")
        .select("id, name, location, latitude, longitude")
        .not("latitude", "is", null)
        .not("longitude", "is", null)
      
      let nearbyActivityTypesMap: Record<string, string | null> = {}
      if (nearbySpots && nearbySpots.length > 0) {
        try {
          const { data: spotsWithActivity } = await supabase
            .from("surf_spots")
            .select("id, activity_type")
            .in("id", nearbySpots.map(s => s.id))
          
          if (spotsWithActivity) {
            spotsWithActivity.forEach((spot: any) => {
              nearbyActivityTypesMap[spot.id] = spot.activity_type || null
            })
          }
        } catch (err) {}
      }

      if (error) throw error

      if (nearbySpots) {
        const nearby = nearbySpots
          .map((spot) => {
            const distance = calculateDistance(
              lat,
              lng,
              Number.parseFloat(spot.latitude as string),
              Number.parseFloat(spot.longitude as string),
            )
            return { ...spot, distance, activity_type: nearbyActivityTypesMap[spot.id] || null }
          })
          .filter((spot) => {
            const isSimilarLocation =
              spot.location.toLowerCase().includes(locationName.toLowerCase()) ||
              locationName.toLowerCase().includes(spot.location.toLowerCase())
            return spot.distance < 1 && isSimilarLocation
          })
          .sort((a, b) => a.distance - b.distance)

        setDuplicates(nearby as DuplicateSpot[])
        setExistingSpot(nearby.length > 0 && nearby[0].distance < 0.5 ? (nearby[0] as DuplicateSpot) : null)
      }
    } catch (error) {
      console.error("Error checking duplicates:", error)
    } finally {
      setIsCheckingDuplicates(false)
    }
  }

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371
    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLon = ((lon2 - lon1) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    triggerHaptic('selection')

    if (!formData.location || !formData.country || !activityType) {
      triggerHaptic('error')
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      })
      return
    }

    setIsSubmitting(true)
    setIsGeneratingDescription(true)

    try {
      let finalDescription = ""
      let aiGeneratedConditions: any = {}
      
      const updatingExisting = existingSpot !== null
      setIsUpdatingExisting(updatingExisting)
      const existingActivityType = existingSpot?.activity_type
      
      let finalActivityType: string
      if (updatingExisting) {
        if (activityType === "both") {
          finalActivityType = "both"
        } else if (existingActivityType === "surfing" && activityType === "kitesurfing") {
          finalActivityType = "both"
        } else if (existingActivityType === "kitesurfing" && activityType === "surfing") {
          finalActivityType = "both"
        } else {
          finalActivityType = activityType
        }
      } else {
        finalActivityType = activityType
      }

      const descriptionResponse = await fetch("/api/perplexity/generate-description", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          location: formData.location,
          country: formData.country,
          activityType: finalActivityType === "both" ? "both" : activityType,
          existingActivityType: updatingExisting ? existingActivityType : undefined,
          isUpdatingExisting: updatingExisting,
          selectedAttributes: {},
          selectedFilters: [],
        }),
      })

      if (!descriptionResponse.ok) throw new Error("AI generation failed")

      const descriptionResult = await descriptionResponse.json()

      if (descriptionResult.success && descriptionResult.data?.description) {
        finalDescription = descriptionResult.data.description
        const extractedConditions = descriptionResult.data.conditions || {}
        aiGeneratedConditions = {
          wave_type: extractedConditions.wave_type || null,
          skill_level: extractedConditions.skill_level || null,
          best_wind_direction: extractedConditions.best_wind_direction || null,
          best_swell_direction: extractedConditions.best_swell_direction || null,
        }
      } else {
        throw new Error("Could not generate AI description")
      }

      setIsGeneratingDescription(false)

      const method = updatingExisting ? "PATCH" : "POST"
      const url = updatingExisting ? `/api/surf-spots/${existingSpot.id}` : "/api/surf-spots"
      
      const { data: { session } } = await supabase.auth.getSession()
      const headers: any = { "Content-Type": "application/json" }
      if (session?.access_token) headers["Authorization"] = `Bearer ${session.access_token}`
      
      const requestBody = {
        name: formData.location,
        location: formData.location,
        country: formData.country,
        latitude: formData.latitude,
        longitude: formData.longitude,
        description: finalDescription,
        image_url: selectedImage?.url || null,
        image_unsplash_id: selectedImage?.unsplashId || null,
        wave_type: aiGeneratedConditions.wave_type || null,
        skill_level: aiGeneratedConditions.skill_level || null,
        best_wind_direction: aiGeneratedConditions.best_wind_direction || null,
        best_swell_direction: aiGeneratedConditions.best_swell_direction || null,
        activity_type: finalActivityType,
        selected_filters: [],
      }
      
      const saveSpotResponse = await csrfFetch(url, {
        method,
        headers,
        credentials: "include",
        body: JSON.stringify(requestBody),
      })

      if (!saveSpotResponse.ok) throw new Error("Failed to save spot")

      const saveResult = await saveSpotResponse.json()
      if (!saveResult.success) throw new Error("Save failed")

      const spot = saveResult.data?.spot
      const spotSlug = spot?.slug || spot?.id

      triggerHaptic('success')
      setCreatedSpotSlug(spotSlug)
      setCreatedActivityType(finalActivityType)
      setShowSuccess(true)
    } catch (error) {
      triggerHaptic('error')
      console.error("Error submitting surf spot:", error)
      toast({
        title: "Submission Failed",
        description: "Failed to create surf spot. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
      setIsGeneratingDescription(false)
    }
  }

  const handleReset = () => {
    setFormData({
      location: "",
      country: "",
      latitude: 0,
      longitude: 0,
      description: "",
    })
    setActivityType(null)
    setSelectedImage(null)
    setDuplicates([])
    setExistingSpot(null)
    setShowSuccess(false)
    setCreatedSpotSlug(null)
    setCreatedActivityType(null)
    setOpen(false)
  }

  const renderContent = () => (
    <div className="p-6 md:p-8 space-y-8 relative">
      {/* Loading Screen */}
      {(isSubmitting || isGeneratingDescription) && !showSuccess && (
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center min-h-[400px]">
          <div className="relative mb-8">
            <div className="text-8xl mb-4 animate-bounce" style={{ animationDuration: '2s' }}>🏄</div>
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-6xl opacity-60">🌊</div>
          </div>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-blue-500 animate-pulse" />
            <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">AI Researching Spot...</h3>
          </div>
          <p className="text-slate-500 dark:text-slate-400 text-sm max-w-md italic">
            Generating a comprehensive description and finding optimal conditions for {formData.location}...
          </p>
          <div className="mt-8 w-full max-w-xs">
            <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden shadow-inner">
              <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: '60%' }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Success Screen */}
      {showSuccess && (
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center min-h-[400px]">
          <div className="mb-8">
            <div className="w-24 h-24 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-green-100 dark:border-green-900/30">
              <div className="text-5xl">🎉</div>
            </div>
            <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-2">
              {isUpdatingExisting ? "Spot Updated!" : "Spot Created!"}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm max-w-xs mx-auto italic leading-relaxed">
              {isUpdatingExisting
                ? `Successfully updated ${formData.location} to support new activities.`
                : `Successfully added ${formData.location} to the global surf database.`}
            </p>
          </div>

          {createdSpotSlug && (
            <div className="w-full space-y-4">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2">Add a review now</p>
              <div className="grid grid-cols-2 gap-3">
                {(createdActivityType === "both" || createdActivityType === "surfing") && (
                  <Button
                    onClick={() => {
                      triggerHaptic('selection')
                      router.push(`/spot/${createdSpotSlug}/review?activity=surfing`)
                      handleReset()
                    }}
                    className="flex-1 h-24 rounded-[2rem] bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-600 border border-blue-100/50 dark:border-blue-900/30 shadow-sm flex flex-col items-center justify-center gap-2 active:scale-95 transition-all"
                  >
                    <span className="text-3xl">🏄</span>
                    <span className="text-[10px] font-black uppercase tracking-widest">Surfing</span>
                  </Button>
                )}
                {(createdActivityType === "both" || createdActivityType === "kitesurfing") && (
                  <Button
                    onClick={() => {
                      triggerHaptic('selection')
                      router.push(`/spot/${createdSpotSlug}/review?activity=kitesurfing`)
                      handleReset()
                    }}
                    className="flex-1 h-24 rounded-[2rem] bg-teal-50 hover:bg-teal-100 dark:bg-teal-900/20 dark:hover:bg-teal-900/30 text-teal-600 border border-teal-100/50 dark:border-teal-900/30 shadow-sm flex flex-col items-center justify-center gap-2 active:scale-95 transition-all"
                  >
                    <span className="text-3xl">🪁</span>
                    <span className="text-[10px] font-black uppercase tracking-widest">Kiting</span>
                  </Button>
                )}
              </div>
              <Button variant="ghost" onClick={handleReset} className="w-full mt-4 text-slate-400 font-bold uppercase tracking-widest text-[10px] h-12 rounded-2xl">
                Maybe Later
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Main Form */}
      {!isSubmitting && !isGeneratingDescription && !showSuccess && (
        <>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-2xl shadow-sm border border-blue-100/50 dark:border-blue-900/30">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Add New Spot</h2>
            </div>
            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mt-1">
              Contribute to the community. AI will generate the details for you.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-6">
              {existingSpot && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                  <div className="text-[11px]">
                    <p className="font-black uppercase tracking-widest text-amber-800 dark:text-amber-400 mb-0.5">Spot Already Exists</p>
                    <p className="text-amber-700/80 dark:text-amber-500/80 font-medium italic">
                      {existingSpot.name} is already listed. You can add more activities to it below.
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Search Location *</Label>
                <LocationSearch
                  onLocationSelect={handleLocationSelect}
                  value={formData.location ? `${formData.location}, ${formData.country}` : ""}
                  className="h-12 rounded-2xl border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 focus:ring-blue-500/20 shadow-sm"
                />
              </div>

              {formData.country && (
                <div className="grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2">
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Country</Label>
                    <div className="h-12 px-4 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800 flex items-center gap-2">
                      <Globe className="w-3.5 h-3.5 text-blue-500" />
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{formData.country}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Coordinates</Label>
                    <div className="h-12 px-4 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800 flex items-center">
                      <span className="text-[10px] font-bold text-slate-400 truncate">
                        {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {formData.location && (
                <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Choose a Photo</Label>
                  <UnsplashImagePicker
                    locationName={formData.location}
                    onImageSelect={setSelectedImage}
                    selectedImageId={selectedImage?.unsplashId}
                  />
                </div>
              )}

              <div className="space-y-4">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">What activity is this spot for? *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {activityTypes.map((activity) => {
                    const existingType = existingSpot?.activity_type
                    const isDisabled =
                      (activity.value === "both" && existingType === "both") ||
                      (activity.value === "surfing" && (existingType === "surfing" || existingType === "both")) ||
                      (activity.value === "kitesurfing" && (existingType === "kitesurfing" || existingType === "both"))
                    
                    return (
                      <div
                        key={activity.value}
                        onClick={() => !isDisabled && (triggerHaptic('impact'), setActivityType(activity.value))}
                        className={`flex flex-col items-center gap-2 p-4 border rounded-2xl cursor-pointer transition-all active:scale-[0.95] ${
                          isDisabled
                            ? "bg-slate-50 dark:bg-slate-900 border-slate-100 dark:border-slate-800 opacity-40 cursor-not-allowed"
                            : activityType === activity.value
                              ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 text-blue-600 shadow-sm"
                              : "border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-950/30 text-slate-400 hover:bg-slate-50"
                        }`}
                      >
                        <span className="text-2xl">{activity.emoji}</span>
                        <span className="text-[10px] font-black uppercase tracking-widest text-center">
                          {activity.label}
                          {isDisabled && <span className="block text-[8px] text-amber-500">(Already Added)</span>}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleReset} 
                className="h-14 flex-1 rounded-[1.25rem] font-bold text-slate-500 border-slate-100 dark:border-slate-800"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting || !activityType || !formData.location || (duplicates.length > 0 && !existingSpot)} 
                className="h-14 flex-[2] rounded-[1.25rem] bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest text-xs shadow-xl shadow-blue-500/25 active:scale-95 transition-all disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Submit Spot
                  </>
                )}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(isOpen) => !isOpen && !isSubmitting ? handleReset() : setOpen(isOpen)}>
        {trigger && <DrawerTrigger asChild>{trigger}</DrawerTrigger>}
        <DrawerContent className="max-h-[95vh] bg-white dark:bg-slate-900 border-none rounded-t-[3rem] z-[200]">
          <div className="mx-auto w-12 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full mt-3 mb-2" />
          <DrawerHeader className="sr-only">
            <DrawerTitle>Add New Surf Spot</DrawerTitle>
            <DrawerDescription>Contribute a new spot to the community</DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto pb-12">
            {renderContent()}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isSubmitting ? handleReset() : setOpen(isOpen)}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900 border-none rounded-[2.5rem] shadow-2xl p-0 gap-0 z-[200]">
        <DialogHeader className="sr-only">
          <DialogTitle>Add New Surf Spot</DialogTitle>
          <DialogDescription>Contribute a new spot to the community</DialogDescription>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  )
}
